/**
 * **What a task card says at a glance** — one big value, what it counts, and one line of context —
 * for every reason the queue can raise, decided once for the three surfaces that draw a task card:
 * `/zadania`, the Pulpit's "Szybki feedback" and the client's Przegląd.
 *
 * Owner, 24 September 2026, against `docs/design/karty-zadan-2026-09-24/`: a card opens with ONE
 * figure that says how big the thing is — "22/24" serii, "9 dni" bez treningu, "13 dni" do końca
 * planu — and the visual under it explains that figure. The figure and its line are a DECISION
 * about the domain (which number stands for a missed session? what does an overdue goal count?),
 * so they are made here, where a test reaches them, and every surface prints the same answer.
 *
 * ── A figure nobody has is not drawn, and never drawn as a dash ──────────────────────────────
 * Every input is nullable on the wire or depends on a read that may not have answered. `hero` is
 * `null` whenever the figure is unknown — a lapse whose last workout the roster does not know, a
 * clip comment with no pinned moment, a plan card before its execution summary lands — and the
 * surface then draws the line alone. "—" would state an absence the trainer can do nothing with.
 *
 * ── Days are CIVIL days ──────────────────────────────────────────────────────────────────────
 * A plan's end, a goal's deadline and a missed session's day are all dates on the client's
 * calendar (attention-queue R10), so the arithmetic is on `YYYY-MM-DD` at UTC midnight and never
 * crosses a DST boundary.
 *
 * Pure: no React, no DOM, no query hook, no i18next import — `t` and the formatters arrive as
 * parameters.
 */

import { formatClock } from '~/entities/exercise-clip';
import { setHeadline, setPositionLabel, type SetPosition } from '~/entities/lift-history';
import { parseDateOnly, parseIsoDate, MS_PER_DAY } from '~/shared/lib/calendarDate';
import { dateTimeFormat, numberFormat } from '~/shared/lib/intlCache';
import type { Translate } from '~/shared/lib/localeTranslate';

import type { AttentionItem } from './attentionItems';
import type { ClipFeedback } from './feedbackShape';
import type { PlanExecution } from './planExecution';
import type { VitalFacts } from './triageShape';

const COPY = 'web:tasks.content';
const SEPARATOR = ' · ';
/** The leads inbox's badge for an address the code never proved — the same words on every surface. */
const UNVERIFIED_EMAIL = 'web:publicForm.leads.badges.unverified';

/** The one big figure. `total` is the small "/24" beside it, `unit` the small "dni". */
export interface TaskHero {
  readonly value: string;
  readonly total: string | null;
  readonly unit: string | null;
  readonly caption: string;
  /** The figure is bad news in itself — a lapse, an overdue goal. Drawn in the danger ink. */
  readonly danger: boolean;
}

export interface TaskContent {
  readonly hero: TaskHero | null;
  /** One line of context under the reason's name. `''` where there is nothing to add. */
  readonly line: string;
  /**
   * The Talia cards' one meta line (owner, 25 September 2026): those cards draw no big figure, so
   * where the figure IS the information it is folded in here ("koniec 2 paź · za 7 dni", "9 dni bez
   * treningu"), and what the card already says elsewhere is left out — the day is the card's corner,
   * a plan's name its cover, the sets the ring and the exercise lines.
   */
  readonly glance: string;
}

export interface TaskContentDeps {
  readonly t: Translate;
  /** The trainer's own today, `YYYY-MM-DD`. */
  readonly todayIso: string;
  /** "wt 22" — a civil day as the big figure of a missed session. */
  readonly formatWeekday: (isoDay: string) => string;
  /** "22 wrz" — a civil day or an instant, in a line. */
  readonly formatDate: (iso: string) => string;
  /** "pon 21 wrz, 18:04" — an instant with its hour. */
  readonly formatDateTime: (iso: string) => string;
  /** The reader's language, for the clip's clock and its set words — the player's own. */
  readonly locale: string;
}

/** The reads a card may have made. Every one is optional; the projection degrades without it. */
export interface TaskContentReads {
  /** Whole days since the client last finished a workout — the roster's. */
  readonly daysSinceLastWorkout?: number;
  /** The civil day of that workout, or `null` for a client who never finished one. */
  readonly lastWorkoutDay?: string | null;
  /**
   * The roster those two come from is still being read. Its own state, not a `null` day: a client
   * whose last workout is not known YET is not a client who never trained, and says nothing.
   */
  readonly rosterPending?: boolean;
  /**
   * The roster read FAILED. Its own state again: a client whose last workout could not be read is
   * not a client who never trained either, and the line says the read failed.
   */
  readonly rosterFailed?: boolean;
  /** The plan's execution, for the plan cards. */
  readonly execution?: PlanExecution | null;
  /** The civil day an invitation was sent — the roster's. */
  readonly invitationSentOn?: string | null;
}

/** Whole civil days from `from` to `to`; `null` where either is unreadable. */
export function civilDaysBetween(from: string, to: string): number | null {
  const a = parseDateOnly(from.slice(0, 10));
  const b = parseDateOnly(to.slice(0, 10));
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / MS_PER_DAY);
}

function joined(...parts: readonly (string | null | undefined)[]): string {
  return parts.filter((part): part is string => part !== null && part !== undefined && part !== '').join(SEPARATOR);
}

const MS_PER_SECOND = 1000;

/**
 * Which set a clip was filmed in, in the PLAYER's words (`entities/lift-history`): „Seria 3 · 140 kg
 * × 5", „Z sesji" for a session with no set, „nagrany poza sesją" for a clip filmed outside one
 * (`exercise-clip-timeline` R1). The queue carries no lift history, so the number is the logger's
 * own and names no total — the player's "not in history" case, so the card and the player the
 * card opens name the set alike.
 */
function clipAnchor(clip: ClipFeedback, t: Translate, locale: string): readonly string[] {
  if (clip.workoutExecutionId === null && clip.setIndex === null) return [t(`${COPY}.clip.unanchored`)];
  const position: SetPosition =
    clip.setIndex === null ? { kind: 'session' } : { kind: 'numbered', number: clip.setIndex };
  const load = setHeadline({ weightKg: clip.weight, reps: clip.reps }, { t, locale });
  const place = setPositionLabel(position, t);
  return load === null ? [place] : [place, load];
}

/** A figure with its caption; no total, no unit and good news unless said otherwise. */
function hero(
  value: string,
  caption: string,
  { total = null, unit = null, danger = false }: Partial<Pick<TaskHero, 'total' | 'unit' | 'danger'>> = {},
): TaskHero {
  return { value, total, unit, caption, danger };
}

function days(t: Translate, count: number): string {
  return t(`${COPY}.days`, { count });
}

/**
 * The Talia cards' meta line when the figure is FOLDED into it: "9 dni bez treningu", "5/12 serii
 * zrobionych" — the figure and what it counts, in one run of words.
 */
function folded(figure: TaskHero): string {
  return [`${figure.value}${figure.total ?? ''}`, figure.unit, figure.caption]
    .filter((part): part is string => part !== null && part !== '')
    .join(' ');
}

/** How far a civil day is from today, worded for a meta line: "za 7 dni", or the figure folded. */
function distance(t: Translate, gap: number, past: TaskHero): string {
  if (gap === 0) return t(`${COPY}.glance.today`);
  return gap > 0 ? t(`${COPY}.glance.inDays`, { n: gap, days: days(t, gap) }) : folded(past);
}

/**
 * "czw., 1 paź" — the day a reading was taken or a record set, with its weekday. A civil day is read
 * verbatim (`parseIsoDate`), an instant in the reader's own zone; an unreadable string comes back as
 * itself, as every date on these cards does.
 */
export function weekdayDate(iso: string, locale: string): string {
  const at = parseIsoDate(iso) ?? new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return dateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(at);
}

/** Which of the two readings a low-reading task is about. */
export type VitalKind = 'sleep' | 'energy';

/** The reading behind a reason, or `null` for a reason that judges none. */
export function vitalKindOf(reason: AttentionItem['reason']): VitalKind | null {
  if (reason === 'lowSleep') return 'sleep';
  return reason === 'lowEnergy' ? 'energy' : null;
}

/**
 * How a reading's figures are spelled, in one place for the heading, the tiles, the plot and the
 * tab. Sleep is hours to one decimal with its unit — an average, a single night and the level alike
 * ("5,7 h", "6,0 h"). Energy is a 1–10 rating: a reading and a level are whole numbers, and only
 * the AVERAGE carries a decimal, because the server keeps it to one more than a reading (R22).
 */
export interface VitalSpelling {
  readonly average: (value: number) => string;
  /** One reading, bare — "5,5", "4" — for a run of them that names the unit once. */
  readonly reading: (value: number) => string;
  readonly level: (value: number) => string;
  /** A figure with the reading's unit after it: "5,5 h"; energy has none. */
  readonly withUnit: (figure: string) => string;
}

export function vitalSpelling(kind: VitalKind, t: Translate, locale: string): VitalSpelling {
  const oneDecimal = numberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  if (kind === 'sleep') {
    const hours = (figure: string) => `${figure} ${t(`${COPY}.vital.hour`)}`;
    return {
      average: (value) => oneDecimal.format(value),
      reading: (value) => oneDecimal.format(value),
      level: (value) => hours(oneDecimal.format(value)),
      withUnit: hours,
    };
  }
  const whole = numberFormat(locale, { maximumFractionDigits: 0 });
  return {
    average: (value) => oneDecimal.format(value),
    reading: (value) => whole.format(value),
    level: (value) => whole.format(value),
    withUnit: (figure) => figure,
  };
}

/** Where the newest reading came from: the survey, or the session — by name where the wire has it. */
function vitalSourceWords(vital: VitalFacts, sessionName: string | null, t: Translate): string | null {
  if (vital.source === 'Survey') return t(`${COPY}.vital.fromSurvey`);
  if (vital.source !== 'Workout') return null;
  return sessionName === null
    ? t(`${COPY}.vital.fromWorkout`)
    : t(`${COPY}.vital.fromWorkoutNamed`, { session: sessionName });
}

/**
 * A low reading (R22, R23). Every number is the server's: the average the item was raised on, how
 * many readings it is taken over and the level it stood under. Nothing is averaged here.
 */
function vitalContent(
  kind: VitalKind,
  vital: VitalFacts,
  sessionName: string | null,
  { t, locale }: TaskContentDeps,
): TaskContent {
  const spell = vitalSpelling(kind, t, locale);
  const level = spell.level(vital.threshold);
  const day = vital.localDay === null ? null : weekdayDate(vital.localDay, locale);
  const source = vitalSourceWords(vital, sessionName, t);
  const average = spell.average(vital.average);
  return {
    hero: hero(average, t(`${COPY}.vital.caption`, { count: vital.sampleCount, level }), {
      unit: kind === 'sleep' ? t(`${COPY}.vital.hour`) : null,
      total: kind === 'energy' ? t(`${COPY}.vital.outOfTen`) : null,
      danger: true,
    }),
    line: joined(day === null ? null : t(`${COPY}.vital.latest`, { day }), source),
    glance: joined(
      t(`${COPY}.vital.glance`, { count: vital.sampleCount, average: spell.withUnit(average) }),
      t(`${COPY}.vital.level`, { level }),
      day === null ? null : [t(`${COPY}.vital.latestGlance`, { day }), source].filter((part) => part !== null).join(', '),
    ),
  };
}

export function taskContent(
  item: AttentionItem,
  deps: TaskContentDeps,
  reads: TaskContentReads = {},
): TaskContent {
  const { t, todayIso, formatWeekday, formatDate, formatDateTime, locale } = deps;
  const facts = item.triage;
  const detail = item.detail;

  switch (item.reason) {
    case 'sessionAwaitingReview': {
      if (detail.kind !== 'session') return { hero: null, line: '', glance: '' };
      const minutes =
        detail.durationSeconds === null ? 0 : Math.round(detail.durationSeconds / 60);
      const duration = minutes > 0 ? t(`${COPY}.minutes`, { count: minutes }) : null;
      const line = joined(
        detail.name,
        item.signalAtUtc === null ? null : formatDate(item.signalAtUtc),
        duration,
      );
      // The sets are the ring's and the exercise lines' on a Talia card; the day is its corner.
      const glance = joined(detail.name, duration);
      if (detail.setsDone === null) return { hero: null, line, glance };
      return {
        hero: hero(String(detail.setsDone), t(`${COPY}.caption.sets`), {
          // A freestyle session has no prescription, so no denominator (completed-session-display R9).
          total: detail.freestyle || detail.setsTotal === null ? null : `/${String(detail.setsTotal)}`,
        }),
        line,
        glance,
      };
    }

    case 'surveyAwaitingReview': {
      if (detail.kind !== 'survey') return { hero: null, line: '', glance: '' };
      const title = item.description.values.survey;
      const name = typeof title === 'string' ? title : t(`${COPY}.survey.${detail.flavour}`);
      const outcome = detail.outcome === null ? null : t(`${COPY}.survey.${detail.outcome}`);
      const line = joined(name, item.signalAtUtc === null ? null : formatDate(item.signalAtUtc), outcome);
      // The answers are the card's table; the day is its corner.
      const glance = joined(name, outcome);
      if (detail.answeredCount === null || detail.answeredCount <= 0) return { hero: null, line, glance };
      return {
        hero: hero(String(detail.answeredCount), t(`${COPY}.caption.answers`, { count: detail.answeredCount })),
        line,
        glance,
      };
    }

    case 'newClip': {
      if (detail.kind !== 'clip') return { hero: null, line: '', glance: '' };
      const line = joined(detail.exerciseName, ...clipAnchor(detail, t, locale));
      // The length is the poster's own badge.
      if (detail.durationSeconds === null) return { hero: null, line, glance: line };
      return {
        hero: hero(formatClock(detail.durationSeconds, false, locale), t(`${COPY}.caption.clip`)),
        line,
        glance: line,
      };
    }

    case 'clipCommentAwaitingReply': {
      if (detail.kind !== 'clipComment') return { hero: null, line: '', glance: '' };
      const line = joined(
        detail.discussionCount === null || detail.discussionCount <= 0
          ? null
          : t(`${COPY}.comment.thread`, { count: detail.discussionCount }),
        detail.performedOn === null ? null : t(`${COPY}.clip.performedOn`, { date: formatDate(detail.performedOn) }),
      );
      if (detail.offsetMs === null || detail.offsetMs < 0) return { hero: null, line, glance: line };
      const moment = formatClock(detail.offsetMs / MS_PER_SECOND, false, locale);
      return {
        hero: hero(moment, t(`${COPY}.caption.question`)),
        line,
        glance: joined(t(`${COPY}.glance.questionAt`, { at: moment }), line),
      };
    }

    case 'abandonedWorkout': {
      const line = joined(
        facts.sessionName,
        item.signalAtUtc === null ? null : t(`${COPY}.startedAt`, { when: formatDateTime(item.signalAtUtc) }),
      );
      if (facts.sessionSetsDone === null) return { hero: null, line, glance: joined(facts.sessionName) };
      const figure = hero(String(facts.sessionSetsDone), t(`${COPY}.caption.sets`), {
        total: facts.sessionSetsTotal === null ? null : `/${String(facts.sessionSetsTotal)}`,
      });
      return { hero: figure, line, glance: joined(facts.sessionName, folded(figure)) };
    }

    case 'missedWorkout': {
      const day = facts.sessionLocalDay;
      const line = joined(facts.sessionName, day === null ? null : formatDate(day));
      // The week strip marks the day itself.
      const glance = joined(facts.sessionName);
      if (day === null) return { hero: null, line, glance };
      return {
        hero: hero(formatWeekday(day), t(`${COPY}.caption.missed`)),
        line,
        glance,
      };
    }

    case 'lapsedActivity': {
      const last = reads.lastWorkoutDay;
      const line =
        reads.rosterPending === true
          ? ''
          : reads.rosterFailed === true
            ? t('web:tasks.blocks.readFailed')
            : last === null || last === undefined
              ? t(`${COPY}.lapsed.never`)
              : t(`${COPY}.lapsed.last`, { date: formatDate(last) });
      const count = reads.daysSinceLastWorkout;
      if (count === undefined) return { hero: null, line, glance: line };
      const figure = hero(String(count), t(`${COPY}.caption.withoutTraining`), { unit: days(t, count), danger: true });
      return { hero: figure, line, glance: joined(folded(figure), line) };
    }

    case 'noUpcomingSessions': {
      // The detector's condition IS "nothing scheduled ahead", so the zero is a fact, not a guess.
      const line = joined(facts.planName === null ? null : t(`${COPY}.plan.running`, { plan: facts.planName }));
      return { hero: hero('0', t(`${COPY}.caption.nothingAhead`)), line, glance: line };
    }

    case 'planEndingSoon':
    case 'planCompletionDue': {
      const end = facts.planEndDate;
      const ends =
        end === null
          ? null
          : t(item.reason === 'planEndingSoon' ? `${COPY}.plan.ends` : `${COPY}.plan.ended`, { date: formatDate(end) });
      const line = joined(facts.planName, ends);
      const gap = end === null ? null : civilDaysBetween(todayIso, end);
      // The plan's name is its cover's on a Talia card.
      if (gap === null) return { hero: null, line, glance: joined(ends) };
      const count = Math.abs(gap);
      const figure = hero(String(count), t(gap >= 0 ? `${COPY}.caption.untilEnd` : `${COPY}.caption.afterEnd`), {
        unit: days(t, count),
      });
      return { hero: figure, line, glance: joined(ends, distance(t, gap, figure)) };
    }

    case 'planCompleted': {
      const line = joined(facts.planName, t(`${COPY}.plan.closedItself`));
      // The sessions are the card's own rows; the name is its cover's.
      const glance = t(`${COPY}.plan.closedItself`);
      const execution = reads.execution;
      if (execution === null || execution === undefined || execution.prescribed <= 0) return { hero: null, line, glance };
      return {
        hero: hero(String(execution.done), t(`${COPY}.caption.sessions`), { total: `/${String(execution.prescribed)}` }),
        line,
        glance,
      };
    }

    case 'goalRenewalDue': {
      const deadline = facts.goalDeadline;
      const due = deadline === null ? null : t(`${COPY}.goal.deadline`, { date: formatDate(deadline) });
      const line = joined(facts.goalDescription, due);
      const gap = deadline === null ? null : civilDaysBetween(todayIso, deadline);
      // The goal's own words are the block's title.
      if (gap === null) return { hero: null, line, glance: joined(due) };
      const count = Math.abs(gap);
      const figure = hero(String(count), t(gap >= 0 ? `${COPY}.caption.untilDeadline` : `${COPY}.caption.pastDeadline`), {
        unit: days(t, count),
        // The detector has no lower bound: a deadline already past is the same task, worse.
        danger: gap < 0,
      });
      return { hero: figure, line, glance: joined(due, distance(t, gap, figure)) };
    }

    case 'invitationPending': {
      const sent = reads.invitationSentOn ?? null;
      const line = sent === null ? '' : t(`${COPY}.invitation.sent`, { date: formatDate(sent) });
      // The days waiting are the envelope's.
      if (facts.daysPending === null) return { hero: null, line, glance: line };
      return {
        hero: hero(String(facts.daysPending), t(`${COPY}.caption.noAnswer`), { unit: days(t, facts.daysPending) }),
        line,
        glance: line,
      };
    }

    case 'unreadMessage': {
      const line = t(`${COPY}.message.line`);
      return { hero: null, line, glance: line };
    }

    case 'goalAchieved': {
      const line = facts.goalDescription ?? '';
      return { hero: null, line, glance: line };
    }

    case 'lowSleep':
    case 'lowEnergy': {
      const kind = vitalKindOf(item.reason);
      if (facts.vital === null || kind === null) return { hero: null, line: '', glance: '' };
      return vitalContent(kind, facts.vital, facts.sessionName, deps);
    }

    case 'prAchieved': {
      // The client's own day the session ended on (R10), where the wire has it.
      const ended = facts.sessionLocalDay ?? item.signalAtUtc;
      const day = ended === null ? null : weekdayDate(ended, locale);
      const session = facts.sessionName === null ? null : t(`${COPY}.record.session`, { session: facts.sessionName });
      const line = joined(session, day);
      const record = facts.record;
      if (record === null) return { hero: null, line, glance: line };
      // The Talia TAB's figure — "3 rekordy"; the desk's heading leaves it to the hero under it.
      const figure = hero(String(record.movements), '', {
        unit: t('web:tasks.blocks.plan.records', { count: record.movements }),
      });
      return {
        hero: figure,
        line,
        glance: joined(folded(figure), [session, day].filter((part) => part !== null).join(', ')),
      };
    }

    // When the form arrived; who sent it is the card's name. An address the code never proved is
    // said in plain words after it, the inbox's own badge text; unknown says nothing.
    case 'publicFormLeadSubmitted': {
      const line = joined(
        item.signalAtUtc === null ? null : formatDateTime(item.signalAtUtc),
        facts.leadEmailVerified === false ? t(UNVERIFIED_EMAIL) : null,
      );
      return { hero: null, line, glance: line };
    }
  }
}

/** How an invitation's offer stands today. */
export interface InvitationValidity {
  /** Whole days until the offer lapses; `0` on its last day. `null` without an expiry. */
  readonly daysLeft: number | null;
  readonly expiresOn: string | null;
  readonly sentOn: string | null;
}

/**
 * The offer's own clock — client-onboarding R4's fourteen days, read off the roster's
 * `invitationExpiresAt`. Never the mailed link's: that is Supabase's setting and no value this app
 * reads states it, so the card does not claim one.
 */
export function invitationValidity(
  clock: { readonly sentAt: string | null; readonly expiresAt: string | null } | null,
  todayIso: string,
): InvitationValidity {
  const expiresOn = clock?.expiresAt?.slice(0, 10) ?? null;
  const sentOn = clock?.sentAt?.slice(0, 10) ?? null;
  const left = expiresOn === null ? null : civilDaysBetween(todayIso, expiresOn);
  return { daysLeft: left === null ? null : Math.max(0, left), expiresOn, sentOn };
}

/**
 * How the client detail draws a client's open tasks (owner, 24 September 2026): NOTHING when there
 * are none — one quiet line says so instead — the card alone for one, and the tabs over the card
 * only from two. A tab strip holding a single tab is a control with nothing to choose.
 */
export type TaliaLayout = 'none' | 'single' | 'tabs';

export function taliaLayout(count: number): TaliaLayout {
  if (count <= 0) return 'none';
  return count === 1 ? 'single' : 'tabs';
}

/**
 * Reasons whose one figure the `/zadania` card's BODY draws in its own place — the session's ring
 * holds the sets in its hole, a plan's cover carries its days, the invitation's envelope its waiting
 * days — so the heading above the body leaves it off rather than saying it twice.
 */
const FIGURE_IN_BODY: ReadonlySet<AttentionItem['reason']> = new Set([
  'sessionAwaitingReview',
  'planEndingSoon',
  'planCompletionDue',
  'planCompleted',
  'invitationPending',
  // The record's hero names the movement and its result; the count is the Talia tab's.
  'prAchieved',
]);

export function figureStandsInBody(reason: AttentionItem['reason']): boolean {
  return FIGURE_IN_BODY.has(reason);
}

/**
 * A record's card opens with the movement's picture, and the kind's heading stands INSIDE that hero
 * — beside the photograph rather than above it (`docs/design/zadania-progi-2026-10-02/`). The desk
 * card asks this so the heading is drawn once.
 */
export function headingStandsInBody(reason: AttentionItem['reason']): boolean {
  return reason === 'prAchieved';
}
